import { useState } from "react";
import { Link } from "react-router-dom";
import { Navbar } from "@/components/Navbar";
import Footer from "@/components/Footer";
import {
  WhatsappLogo, InstagramLogo, LinkedinLogo, EnvelopeSimple, ArrowRight,
  Sparkle, Clock, ShieldCheck, Copy, Check,
} from "@phosphor-icons/react";
import {
  WHATSAPP_URL, INSTAGRAM_URL, LINKEDIN_URL, MAILTO_URL,
  INSTAGRAM_HANDLE, LINKEDIN_HANDLE, SUPPORT_EMAIL, WHATSAPP_NUMBER,
  trackContactClick,
} from "@/lib/contact";
import { toast } from "sonner";

const cards = [
  {
    id: "whatsapp",
    icon: WhatsappLogo,
    title: "WhatsApp",
    line: "Fastest replies. Tap to open the chat.",
    cta: "Chat on WhatsApp",
    handle: WHATSAPP_NUMBER,
    href: WHATSAPP_URL,
    accent: { ring: "from-emerald-400/40", chip: "bg-emerald-500/10 border-emerald-500/30 text-emerald-300", btn: "bg-emerald-500 hover:bg-emerald-400 text-black", icon: "text-emerald-400" },
    external: true,
  },
  {
    id: "instagram",
    icon: InstagramLogo,
    title: "Instagram",
    line: "DMs, tips and product updates.",
    cta: "Follow Us",
    handle: `@${INSTAGRAM_HANDLE}`,
    href: INSTAGRAM_URL,
    accent: { ring: "from-pink-400/40", chip: "bg-pink-500/10 border-pink-500/30 text-pink-300", btn: "bg-gradient-to-tr from-pink-500 via-fuchsia-500 to-orange-400 text-white hover:opacity-90", icon: "text-pink-400" },
    external: true,
  },
  {
    id: "linkedin",
    icon: LinkedinLogo,
    title: "LinkedIn",
    line: "Connect with us and follow Career GPS AI updates.",
    cta: "Connect on LinkedIn",
    handle: LINKEDIN_HANDLE,
    href: LINKEDIN_URL,
    accent: { ring: "from-sky-400/40", chip: "bg-sky-500/10 border-sky-500/30 text-sky-300", btn: "bg-sky-600 hover:bg-sky-500 text-white", icon: "text-sky-400" },
    external: true,
  },
];

export default function Contact() {
  const [copied, setCopied] = useState(null);

  const copyEmail = async () => {
    try {
      await navigator.clipboard.writeText(SUPPORT_EMAIL);
      setCopied("email");
      toast.success("Email copied");
      setTimeout(() => setCopied(null), 1800);
    } catch (err) {
      console.debug("clipboard copy failed", err);
      toast.error("Couldn't copy");
    }
  };

  return (
    <div className="min-h-screen bg-[#050505] text-white" data-testid="contact-page">
      <Navbar />

      {/* HERO */}
      <section className="relative pt-28 sm:pt-32 md:pt-40 pb-16 sm:pb-20 overflow-hidden">
        <div className="absolute inset-0 -z-0 pointer-events-none">
          <div className="absolute -top-32 -left-20 w-[420px] h-[420px] rounded-full bg-blue-600/10 blur-3xl" />
          <div className="absolute top-1/3 right-0 w-[360px] h-[360px] rounded-full bg-pink-500/10 blur-3xl" />
        </div>

        <div className="relative z-10 max-w-[1400px] mx-auto px-4 sm:px-6 md:px-12 lg:px-24">
          <div className="inline-flex items-center gap-2 bg-blue-500/10 border border-blue-500/30 px-3 py-1.5 rounded-full text-xs mb-6 animate-fade-up">
            <Sparkle size={12} weight="fill" className="text-blue-400" />
            <span className="tracking-wide text-blue-200">We&apos;re humans. We reply.</span>
          </div>
          <h1
            className="text-4xl sm:text-5xl md:text-6xl tracking-tighter font-medium leading-[1.05] max-w-3xl animate-fade-up"
            style={{ animationDelay: "0.05s" }}
            data-testid="contact-heading"
          >
            Need Help With Your <span className="gradient-text">Career Journey?</span>
          </h1>
          <p
            className="mt-5 sm:mt-6 text-base sm:text-lg text-zinc-400 max-w-2xl animate-fade-up"
            style={{ animationDelay: "0.15s" }}
            data-testid="contact-subtitle"
          >
            Our team is here to help you with career guidance, resumes, interviews, and platform support.
            Pick the channel that suits you — we usually respond within a few hours.
          </p>

          {/* Quick promises */}
          <div className="mt-8 flex flex-wrap gap-3 sm:gap-4 text-xs text-zinc-400">
            <div className="inline-flex items-center gap-1.5 bg-white/5 border border-white/10 rounded-full px-3 py-1.5">
              <Clock size={12} className="text-blue-400" /> Avg. reply &lt; 6h
            </div>
            <div className="inline-flex items-center gap-1.5 bg-white/5 border border-white/10 rounded-full px-3 py-1.5">
              <ShieldCheck size={12} className="text-emerald-400" /> No bots — real humans
            </div>
          </div>
        </div>
      </section>

      {/* CARDS */}
      <section className="px-4 sm:px-6 md:px-12 lg:px-24 max-w-[1400px] mx-auto pb-16 sm:pb-24">
        <div className="grid gap-4 sm:gap-6 sm:grid-cols-2 lg:grid-cols-3" data-testid="contact-cards">
          {cards.map((c, idx) => (
            <a
              key={c.id}
              href={c.href}
              target={c.external ? "_blank" : undefined}
              rel={c.external ? "noopener noreferrer" : undefined}
              onClick={() => trackContactClick(c.id, "contact-page")}
              data-testid={`contact-card-${c.id}`}
              className="group relative overflow-hidden bg-[#0F0F12]/85 backdrop-blur-xl border border-white/10 rounded-2xl p-6 sm:p-7 transition-all duration-300 hover:-translate-y-1 hover:border-white/25 animate-fade-up"
              style={{ animationDelay: `${0.2 + idx * 0.08}s` }}
            >
              {/* glow ring */}
              <div className={`pointer-events-none absolute -inset-px rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-300 bg-gradient-to-br ${c.accent.ring} to-transparent blur-xl`} aria-hidden="true" />
              <div className="relative">
                <div className="flex items-start justify-between gap-3">
                  <div className={`w-12 h-12 rounded-xl bg-white/[0.04] border border-white/10 flex items-center justify-center ${c.accent.icon}`}>
                    <c.icon size={24} weight="fill" />
                  </div>
                  <span className={`text-[10px] tracking-[0.2em] uppercase font-semibold px-2 py-1 rounded border ${c.accent.chip}`}>{c.title}</span>
                </div>
                <h3 className="text-xl font-medium mt-5">{c.title}</h3>
                <p className="text-sm text-zinc-400 mt-1 leading-relaxed">{c.line}</p>
                <div className="mt-4 text-xs sm:text-sm text-zinc-300 font-mono break-all">{c.handle}</div>

                <div className="mt-6 flex items-center justify-between gap-3">
                  <span className={`inline-flex items-center gap-2 ${c.accent.btn} font-medium px-4 py-2.5 rounded-lg text-sm transition-all`}>
                    {c.cta} <ArrowRight size={14} className="group-hover:translate-x-0.5 transition-transform" />
                  </span>
                  {c.id === "email" && (
                    <button
                      type="button"
                      onClick={(e) => { e.preventDefault(); e.stopPropagation(); copyEmail(); }}
                      data-testid="contact-email-copy"
                      aria-label="Copy email address"
                      className="text-zinc-400 hover:text-white w-9 h-9 inline-flex items-center justify-center rounded-md border border-white/10 hover:border-white/30 transition-colors"
                    >
                      {copied === "email" ? <Check size={14} className="text-emerald-400" /> : <Copy size={14} />}
                    </button>
                  )}
                </div>
              </div>
            </a>
          ))}
        </div>

        {/* Email-only emphasis block (clickable address + button row) */}
        <div className="mt-8 sm:mt-10 bg-[#0F0F12] border border-white/10 rounded-2xl p-5 sm:p-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4" data-testid="contact-email-block">
          <div className="flex items-center gap-3 min-w-0">
            <div className="w-10 h-10 rounded-lg bg-blue-500/15 border border-blue-500/30 flex items-center justify-center text-blue-400 shrink-0">
              <EnvelopeSimple size={20} weight="fill" />
            </div>
            <div className="min-w-0">
              <div className="text-[10px] tracking-[0.2em] uppercase text-zinc-500 font-semibold">Email us directly</div>
              <a
                href={MAILTO_URL}
                onClick={() => trackContactClick("email", "contact-email-block")}
                data-testid="contact-email-link"
                className="text-base sm:text-lg text-white font-medium hover:text-blue-300 transition-colors break-all"
              >
                {SUPPORT_EMAIL}
              </a>
            </div>
          </div>
          <a
            href={MAILTO_URL}
            onClick={() => trackContactClick("email", "contact-email-block")}
            data-testid="contact-email-send-btn"
            className="inline-flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-500 text-white font-medium px-5 py-3 rounded-lg transition-colors shrink-0"
          >
            <EnvelopeSimple size={16} weight="fill" /> Send Email
          </a>
        </div>

        {/* Helpful nudges */}
        <div className="mt-10 grid sm:grid-cols-2 gap-4">
          <div className="bg-[#0F0F12] border border-white/10 rounded-xl p-5">
            <div className="text-[10px] tracking-[0.2em] uppercase text-blue-400 font-semibold">// Power user tip</div>
            <p className="text-sm text-zinc-300 mt-2 leading-relaxed">
              For fastest help, include your account email and a screenshot of the issue.
              Stuck on something specific? Try the AI Coach in your dashboard first — it can resolve most career questions instantly.
            </p>
            <Link to="/coach" className="mt-3 inline-flex items-center gap-1 text-xs text-blue-400 hover:text-blue-300" data-testid="contact-link-coach">
              Open AI Coach →
            </Link>
          </div>
          <div className="bg-[#0F0F12] border border-white/10 rounded-xl p-5">
            <div className="text-[10px] tracking-[0.2em] uppercase text-blue-400 font-semibold">// Looking for something else?</div>
            <p className="text-sm text-zinc-300 mt-2 leading-relaxed">
              Want to upgrade or check your subscription? You can manage everything from the pricing page.
            </p>
            <Link to="/pricing" className="mt-3 inline-flex items-center gap-1 text-xs text-blue-400 hover:text-blue-300" data-testid="contact-link-pricing">
              View plans →
            </Link>
          </div>
        </div>
      </section>

      <Footer source="contact-footer" />
    </div>
  );
}
